interface EmailData {
  subject: string;
  content: string;
  from?: string;
  name?: string;
  replyTo?: string;
}

interface EmailResponse {
  success: boolean;
  messageId?: string;
  message?: string;
  error?: string;
  details?: string;
}

class EmailService {
  private baseUrl: string;
  private isProduction: boolean;
  private apiKey: string;

  constructor() {
    // Read base URL from environment variable with fallbacks
    this.baseUrl = import.meta.env.VITE_EMAIL_API_URL || 'http://localhost:3000';
    
    // Read API key from environment variable
    this.apiKey = import.meta.env.VITE_EMAIL_API_KEY || '';
    
    // Check if we're in production environment
    this.isProduction = import.meta.env.VITE_ENVIRONMENT === 'production';
    if(!this.isProduction) {
      console.log('isProduction:', this.isProduction);
    }
  }

  async sendEmail(emailData: EmailData): Promise<EmailResponse> {
    // In non-production environments, display email content instead of sending
    if (!this.isProduction) {
      // Display email content in UI modal
      this.displayEmailInUI(emailData);
      
      return {
        success: true,
        messageId: 'dev-mode-' + Date.now(),
        message: 'Email content displayed in UI (development mode)',
        details: 'In production, this email would be sent via the API'
      };
    }

    // Production mode - send actual email
    try {
      if (!this.apiKey) {
        throw new Error('API key not configured. Please set VITE_EMAIL_API_KEY environment variable.');
      }

      const response = await fetch(`${this.baseUrl}/api/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.apiKey,
        },
        body: JSON.stringify(emailData),
      });

      const result: EmailResponse = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send email');
      }

      return result;
    } catch (error) {
      console.error('Email service error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  // Convenience method for contact form emails
  async sendContactForm(data: {
    name: string;
    email: string;
    subject: string;
    message: string;
    company?: string;
    phone?: string;
  }): Promise<EmailResponse> {
    const emailContent = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #333; border-bottom: 2px solid #007bff; padding-bottom: 10px;">
          New Contact Form Submission
        </h2>
        
        <div style="background-color: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <h3 style="color: #495057; margin-top: 0;">Contact Information</h3>
          <p><strong>Name:</strong> ${data.name}</p>
          <p><strong>Email:</strong> ${data.email}</p>
          ${data.company ? `<p><strong>Company:</strong> ${data.company}</p>` : ''}
          ${data.phone ? `<p><strong>Phone:</strong> ${data.phone}</p>` : ''}
        </div>

        <div style="background-color: #ffffff; padding: 20px; border: 1px solid #dee2e6; border-radius: 8px;">
          <h3 style="color: #495057; margin-top: 0;">Message</h3>
          <p style="line-height: 1.6; color: #6c757d;">${data.message.replace(/\n/g, '<br>')}</p>
        </div>

        <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #dee2e6; color: #6c757d; font-size: 14px;">
          <p>This email was sent from the Eceptionist website contact form.</p>
          <p>Reply directly to this email to respond to ${data.name}.</p>
        </div>
      </div>
    `;

    return this.sendEmail({
      subject: `Contact Form: ${data.subject}`,
      content: emailContent,
      replyTo: data.email,
      name: data.name
    });
  }

  // Convenience method for newsletter signup
  async sendNewsletterSignup(email: string, name?: string): Promise<EmailResponse> {
    const emailContent = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #333; border-bottom: 2px solid #007bff; padding-bottom: 10px;">
          New Newsletter Signup
        </h2>
        
        <div style="background-color: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <p><strong>Email:</strong> ${email}</p>
          ${name ? `<p><strong>Name:</strong> ${name}</p>` : ''}
        </div>

        <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #dee2e6; color: #6c757d; font-size: 14px;">
          <p>This email was sent from the Eceptionist website newsletter signup form.</p>
        </div>
      </div>
    `;

    return this.sendEmail({
      subject: 'New Newsletter Signup',
      content: emailContent
    });
  }

  // Demo request form
  async sendDemoRequest(data: {
    name: string;
    email: string;
    organization: string;
    phone?: string;
    service: string;
    message: string;
  }, source?: string): Promise<EmailResponse> {
    const emailContent = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #333; border-bottom: 2px solid #007bff; padding-bottom: 10px;">
          New Demo Request
        </h2>
        
        <div style="background-color: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <h3 style="color: #495057; margin-top: 0;">Contact Information</h3>
          <p><strong>Name:</strong> ${data.name}</p>
          <p><strong>Email:</strong> ${data.email}</p>
          <p><strong>Organization:</strong> ${data.organization}</p>
          ${data.phone ? `<p><strong>Phone:</strong> ${data.phone}</p>` : ''}
          <p><strong>Service Interest:</strong> ${data.service}</p>
          ${source ? `<p><strong>Source:</strong> ${source}</p>` : ''}
        </div>

        <div style="background-color: #ffffff; padding: 20px; border: 1px solid #dee2e6; border-radius: 8px;">
          <h3 style="color: #495057; margin-top: 0;">Message</h3>
          <p style="line-height: 1.6; color: #6c757d;">${data.message.replace(/\n/g, '<br>')}</p>
        </div>

        <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #dee2e6; color: #6c757d; font-size: 14px;">
          <p>This email was sent from the Eceptionist website demo request form.</p>
          <p>Reply directly to this email to respond to ${data.name}.</p>
        </div>
      </div>
    `;

    return this.sendEmail({
      subject: `Demo Request: ${data.service}`,
      content: emailContent,
      replyTo: data.email,
      name: data.name
    });
  }

  // Download form
  async sendDownloadForm(data: {
    name: string;
    email: string;
    organization: string;
    phone?: string;
    paperTitle: string;
    pdfPath: string;
  }, source?: string): Promise<EmailResponse> {
    const emailContent = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #333; border-bottom: 2px solid #007bff; padding-bottom: 10px;">
          White Paper Download Request
        </h2>
        
        <div style="background-color: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <h3 style="color: #495057; margin-top: 0;">Contact Information</h3>
          <p><strong>Name:</strong> ${data.name}</p>
          <p><strong>Email:</strong> ${data.email}</p>
          <p><strong>Organization:</strong> ${data.organization}</p>
          ${data.phone ? `<p><strong>Phone:</strong> ${data.phone}</p>` : ''}
          <p><strong>Paper Title:</strong> ${data.paperTitle}</p>
          ${source ? `<p><strong>Source:</strong> ${source}</p>` : ''}
        </div>

        <div style="background-color: #ffffff; padding: 20px; border: 1px solid #dee2e6; border-radius: 8px;">
          <h3 style="color: #495057; margin-top: 0;">Download Details</h3>
          <p style="line-height: 1.6; color: #6c757d;">
            The user has requested to download: <strong>${data.paperTitle}</strong>
          </p>
          <p style="line-height: 1.6; color: #6c757d;">
            PDF Path: ${data.pdfPath}
          </p>
        </div>

        <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #dee2e6; color: #6c757d; font-size: 14px;">
          <p>This email was sent from the Eceptionist website white paper download form.</p>
          <p>Reply directly to this email to respond to ${data.name}.</p>
        </div>
      </div>
    `;

    return this.sendEmail({
      subject: `White Paper Download: ${data.paperTitle}`,
      content: emailContent,
      replyTo: data.email,
      name: data.name
    });
  }

  // Partnership form submission
  async sendPartnershipForm(data: {
    name: string;
    email: string;
    organization: string;
    partnershipType?: string;
    message?: string;
  }, source?: string): Promise<EmailResponse> {
    const emailContent = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #333; border-bottom: 2px solid #007bff; padding-bottom: 10px;">
          New Partnership Request
        </h2>
        
        <div style="background-color: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <h3 style="color: #495057; margin-top: 0;">Contact Information</h3>
          <p><strong>Name:</strong> ${data.name}</p>
          <p><strong>Email:</strong> ${data.email}</p>
          <p><strong>Organization:</strong> ${data.organization}</p>
          ${data.partnershipType ? `<p><strong>Partnership Type:</strong> ${data.partnershipType}</p>` : ''}
          ${source ? `<p><strong>Source:</strong> ${source}</p>` : ''}
        </div>

        <div style="background-color: #ffffff; padding: 20px; border: 1px solid #dee2e6; border-radius: 8px;">
          <h3 style="color: #495057; margin-top: 0;">Message</h3>
          <p style="line-height: 1.6; color: #6c757d;">${data.message || 'Partnership inquiry submitted'}</p>
        </div>

        <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #dee2e6; color: #6c757d; font-size: 14px;">
          <p>This email was sent from the Eceptionist website partnership form.</p>
          <p>Reply directly to this email to respond to ${data.name}.</p>
        </div>
      </div>
    `;

    return this.sendEmail({
      subject: `Partnership Request: ${data.partnershipType || 'General'}`,
      content: emailContent,
      replyTo: data.email,
      name: data.name
    });
  }

  // Support form submission
  async sendSupportForm(data: {
    name: string;
    email: string;
    organization: string;
    clientName: string;
    phone?: string;
    priority: string;
    category: string;
    subject: string;
    message: string;
  }, source?: string): Promise<EmailResponse> {
    const priorityLabels = {
      low: 'Low - General inquiry',
      medium: 'Medium - Standard issue', 
      high: 'High - Urgent issue',
      urgent: 'Urgent - Critical issue'
    };

    const categoryLabels = {
      general: 'General Support',
      technical: 'Technical Issue',
      billing: 'Billing Question',
      'feature-request': 'Feature Request',
      'bug-report': 'Bug Report'
    };

    const priorityColor = {
      low: '#28a745',
      medium: '#ffc107', 
      high: '#fd7e14',
      urgent: '#dc3545'
    };

    const emailContent = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #333; border-bottom: 2px solid #007bff; padding-bottom: 10px;">
          New Support Request
        </h2>
        
        <div style="background-color: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <h3 style="color: #495057; margin-top: 0;">Contact Information</h3>
          <p><strong>Name:</strong> ${data.name}</p>
          <p><strong>Email:</strong> ${data.email}</p>
          <p><strong>Organization:</strong> ${data.organization}</p>
          <p><strong>Client Name:</strong> ${data.clientName}</p>
          ${data.phone ? `<p><strong>Phone:</strong> ${data.phone}</p>` : ''}
          ${source ? `<p><strong>Source:</strong> ${source}</p>` : ''}
        </div>

        <div style="background-color: #ffffff; padding: 20px; border: 1px solid #dee2e6; border-radius: 8px; margin: 20px 0;">
          <h3 style="color: #495057; margin-top: 0;">Support Details</h3>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 15px;">
            <div>
              <strong>Priority:</strong> 
              <span style="background-color: ${priorityColor[data.priority as keyof typeof priorityColor]}; color: white; padding: 2px 8px; border-radius: 4px; font-size: 12px; margin-left: 5px;">
                ${priorityLabels[data.priority as keyof typeof priorityLabels] || data.priority}
              </span>
            </div>
            <div>
              <strong>Category:</strong> ${categoryLabels[data.category as keyof typeof categoryLabels] || data.category}
            </div>
          </div>
          <p><strong>Subject:</strong> ${data.subject}</p>
        </div>

        <div style="background-color: #ffffff; padding: 20px; border: 1px solid #dee2e6; border-radius: 8px;">
          <h3 style="color: #495057; margin-top: 0;">Message</h3>
          <p style="line-height: 1.6; color: #6c757d;">${data.message.replace(/\n/g, '<br>')}</p>
        </div>

        <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #dee2e6; color: #6c757d; font-size: 14px;">
          <p>This email was sent from the Eceptionist website support form.</p>
          <p>Reply directly to this email to respond to ${data.name}.</p>
          <p><strong>Action Required:</strong> Please respond within 24 hours based on the priority level.</p>
        </div>
      </div>
    `;

    return this.sendEmail({
      subject: `[Support ${data.priority.toUpperCase()}] ${data.subject}`,
      content: emailContent,
      replyTo: data.email,
      name: data.name
    });
  }

  // ROI Calculator form submission
  async sendROICalculator(data: {
    name: string;
    email: string;
    organization: string;
    phone?: string;
    focus?: string;
    automationLevel?: string;
    inputs?: Record<string, any>;
  }, source?: string): Promise<EmailResponse> {
    const emailContent = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #333; border-bottom: 2px solid #007bff; padding-bottom: 10px;">
          ROI Calculator Submission
        </h2>
        
        <div style="background-color: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <h3 style="color: #495057; margin-top: 0;">Contact Information</h3>
          <p><strong>Name:</strong> ${data.name}</p>
          <p><strong>Email:</strong> ${data.email}</p>
          <p><strong>Organization:</strong> ${data.organization}</p>
          ${data.phone ? `<p><strong>Phone:</strong> ${data.phone}</p>` : ''}
          ${source ? `<p><strong>Source:</strong> ${source}</p>` : ''}
        </div>

        <div style="background-color: #ffffff; padding: 20px; border: 1px solid #dee2e6; border-radius: 8px;">
          <h3 style="color: #495057; margin-top: 0;">ROI Calculator Details</h3>
          ${data.focus ? `<p><strong>Focus Area:</strong> ${data.focus}</p>` : ''}
          ${data.automationLevel ? `<p><strong>Automation Level:</strong> ${data.automationLevel}</p>` : ''}
          ${data.inputs ? `
            <h4 style="color: #495057; margin-top: 15px;">Input Parameters:</h4>
            <ul style="margin: 10px 0; padding-left: 20px;">
              ${Object.entries(data.inputs).map(([key, value]) => `<li><strong>${key}:</strong> ${value}</li>`).join('')}
            </ul>
          ` : ''}
        </div>

        <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #dee2e6; color: #6c757d; font-size: 14px;">
          <p>This email was sent from the Eceptionist website ROI calculator.</p>
          <p>Reply directly to this email to respond to ${data.name}.</p>
        </div>
      </div>
    `;

    return this.sendEmail({
      subject: `ROI Calculator Submission: ${data.focus || 'General'}`,
      content: emailContent,
      replyTo: data.email,
      name: data.name
    });
  }

  // Utility method to check current environment
  getEnvironment(): string {
    return this.isProduction ? 'production' : 'development';
  }

  // Utility method to force development mode (useful for testing)
  setDevelopmentMode(): void {
    this.isProduction = false;
  }

  // Utility method to force production mode
  setProductionMode(): void {
    this.isProduction = true;
  }

  // Display email content in UI modal
  private displayEmailInUI(emailData: EmailData): void {
    // Remove any existing email modal
    const existingModal = document.getElementById('email-preview-modal');
    if (existingModal) {
      existingModal.remove();
    }

    // Create modal overlay
    const modal = document.createElement('div');
    modal.id = 'email-preview-modal';
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.8);
      z-index: 10000;
      display: flex;
      justify-content: center;
      align-items: center;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    `;

    // Create modal content
    const modalContent = document.createElement('div');
    modalContent.style.cssText = `
      background: white;
      border-radius: 12px;
      padding: 0;
      max-width: 90%;
      max-height: 90%;
      width: 800px;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
      overflow: hidden;
      display: flex;
      flex-direction: column;
    `;

    // Create header
    const header = document.createElement('div');
    header.style.cssText = `
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      padding: 20px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    `;
    header.innerHTML = `
      <div>
        <h2 style="margin: 0; font-size: 18px; font-weight: 600;">📧 Email Preview (Development Mode)</h2>
        <p style="margin: 5px 0 0 0; opacity: 0.9; font-size: 14px;">This email would be sent in production</p>
      </div>
      <button id="close-email-modal" style="
        background: rgba(255, 255, 255, 0.2);
        border: none;
        color: white;
        width: 32px;
        height: 32px;
        border-radius: 50%;
        cursor: pointer;
        font-size: 18px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background-color 0.2s;
      ">×</button>
    `;

    // Create email details section
    const emailDetails = document.createElement('div');
    emailDetails.style.cssText = `
      padding: 20px;
      border-bottom: 1px solid #e5e7eb;
      background: #f9fafb;
    `;
    
    emailDetails.innerHTML = `
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; font-size: 14px;">
        <div><strong>To:</strong> Configured in API (EMAIL_TO)</div>
        <div><strong>Subject:</strong> ${emailData.subject}</div>
        <div><strong>From:</strong> ${emailData.from || 'Default sender'}</div>
        <div><strong>Reply-To:</strong> ${emailData.replyTo || 'Not specified'}</div>
        ${emailData.name ? `<div><strong>Name:</strong> ${emailData.name}</div>` : ''}
      </div>
    `;

    // Create email content section
    const emailContent = document.createElement('div');
    emailContent.style.cssText = `
      padding: 20px;
      flex: 1;
      overflow-y: auto;
      background: white;
    `;

    // Create tabs for different views
    const tabContainer = document.createElement('div');
    tabContainer.style.cssText = `
      margin-bottom: 20px;
      border-bottom: 1px solid #e5e7eb;
    `;

    const tabs = document.createElement('div');
    tabs.style.cssText = `
      display: flex;
      gap: 0;
    `;

    const previewTab = document.createElement('button');
    previewTab.textContent = 'Preview';
    previewTab.style.cssText = `
      padding: 10px 20px;
      border: none;
      background: #3b82f6;
      color: white;
      cursor: pointer;
      border-radius: 6px 6px 0 0;
      font-size: 14px;
      font-weight: 500;
    `;

    const htmlTab = document.createElement('button');
    htmlTab.textContent = 'HTML Source';
    htmlTab.style.cssText = `
      padding: 10px 20px;
      border: none;
      background: #6b7280;
      color: white;
      cursor: pointer;
      border-radius: 6px 6px 0 0;
      font-size: 14px;
      font-weight: 500;
    `;

    // Create content areas
    const previewContent = document.createElement('div');
    previewContent.style.cssText = `
      border: 1px solid #e5e7eb;
      border-radius: 0 0 6px 6px;
      padding: 20px;
      background: white;
      min-height: 300px;
    `;
    previewContent.innerHTML = emailData.content;

    const htmlContent = document.createElement('div');
    htmlContent.style.cssText = `
      border: 1px solid #e5e7eb;
      border-radius: 0 0 6px 6px;
      padding: 20px;
      background: #f8f9fa;
      min-height: 300px;
      font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
      font-size: 12px;
      white-space: pre-wrap;
      overflow-x: auto;
      display: none;
    `;
    htmlContent.textContent = emailData.content;

    // Tab switching functionality
    previewTab.onclick = () => {
      previewTab.style.background = '#3b82f6';
      htmlTab.style.background = '#6b7280';
      previewContent.style.display = 'block';
      htmlContent.style.display = 'none';
    };

    htmlTab.onclick = () => {
      htmlTab.style.background = '#3b82f6';
      previewTab.style.background = '#6b7280';
      previewContent.style.display = 'none';
      htmlContent.style.display = 'block';
    };

    // Assemble modal
    tabs.appendChild(previewTab);
    tabs.appendChild(htmlTab);
    tabContainer.appendChild(tabs);
    
    emailContent.appendChild(tabContainer);
    emailContent.appendChild(previewContent);
    emailContent.appendChild(htmlContent);

    modalContent.appendChild(header);
    modalContent.appendChild(emailDetails);
    modalContent.appendChild(emailContent);

    modal.appendChild(modalContent);
    document.body.appendChild(modal);

    // Close modal functionality
    const closeModal = () => {
      modal.remove();
    };

    document.getElementById('close-email-modal')!.onclick = closeModal;
    modal.onclick = (e) => {
      if (e.target === modal) {
        closeModal();
      }
    };

    // ESC key to close
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeModal();
        document.removeEventListener('keydown', handleKeyDown);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
  }

  // Preview email content without sending (useful for debugging)
  previewEmail(emailData: EmailData): void {
    this.displayEmailInUI(emailData);
  }

}

export const emailService = new EmailService();
export type { EmailData, EmailResponse };